import { createFileRoute, Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { ArrowUpRight, Compass } from "lucide-react";

import { useMeetingsList } from "@/application/use-cases/meetings/use-meetings.queries";
import { RouteHero } from "@/presentation/components/shell/route-hero";

export const Route = createFileRoute("/_app/discoveries/")({
  component: DiscoveriesIndexPage,
});

function DiscoveriesIndexPage() {
  const meetingsQ = useMeetingsList();
  const meetings = (meetingsQ.data ?? [])
    .filter((m) => !!m.slug)
    .sort((a, b) => (a.meeting_at < b.meeting_at ? -1 : 1));
  const latestMeeting = meetings[meetings.length - 1];

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-6xl flex-col gap-8 p-6 lg:gap-12 lg:p-10">
      <RouteHero
        eyebrow="Discoveries"
        icon={Compass}
        numeral={String(meetings.length).padStart(2, "0")}
        numeralTone="cyan"
        numeralSuffix="conversations"
        title="What we've discovered"
        summary="Each card opens a walkthrough cut of the meeting — the features we'd build, the user flow we've mapped, and what's still open. The raw, unabridged write-up sits one click deeper."
        meta={[
          { label: "Discoveries", value: meetings.length, tone: "cyan" },
          {
            label: "Latest",
            value: latestMeeting ? format(new Date(latestMeeting.meeting_at), "d MMM") : "—",
          },
        ]}
      />

      {meetings.length === 0 ? (
        <p className="text-muted-foreground text-xs italic">No discoveries yet.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {meetings.map((m) => (
            <Link
              key={m.id}
              to="/discoveries/$slug"
              params={{ slug: m.slug }}
              className="glow-card group flex flex-col gap-3 p-5 transition hover:border-[oklch(0.72_0.17_190_/_0.5)] print:break-inside-avoid"
            >
              <span className="font-data text-muted-foreground text-[10px] tracking-[0.18em] uppercase">
                {format(new Date(m.meeting_at), "EEE d MMM yyyy")}
              </span>
              <h2 className="text-base leading-snug font-semibold tracking-tight">{m.title}</h2>
              <span className="text-muted-foreground group-hover:text-foreground mt-auto inline-flex items-center gap-1 text-xs">
                Walk through <ArrowUpRight className="size-3.5" />
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
